import { baseApi } from '@/store/baseApi';
import { CacheDurations } from '@web/utils';

import type {
  StorageUploadRequestPayload,
  StorageUploadResponse,
} from '@/features/storage/types';
import type {
  PatchCurrentWorkspacePayload,
  CurrentWorkspaceResponse,
  WorkspaceIndustryOption,
  WorkspaceCurrencyOption,
  UpdateCurrentWorkspacePayload,
  UpdateCurrentWorkspaceResponse,
  InviteMemberPayload,
  InviteMemberResponse,
  GetUserInvitesResponse,
  AcceptInvitePayload,
  AcceptInviteResponse,
  WorkspaceInviteResolveResponse,
  CreateJoinRequestPayload,
  CreateJoinRequestResponse,
  CreateWorkspacePayload,
  CreateWorkspaceResponse,
  ReactivateArchivedWorkspaceResponse,
  GetWorkspaceInvitesParams,
  GetWorkspaceInvitesResponse,
  GetWorkspaceJoinRequestsParams,
  GetWorkspaceJoinRequestsResponse,
  WorkspaceJoinRequestDecisionPayload,
  WorkspaceJoinRequestDecisionResponse,
  GetWorkspaceMembersParams,
  GetWorkspaceMembersResponse,
  UpdateWorkspaceMemberPayload,
  WorkspaceMemberItem,
  WorkspaceListResponse,
  WorkspacePermissionCheckPayload,
  WorkspacePermissionCheckResponse,
} from '@/features/workspaces/types';
import type { APIResponse } from '@web/types';

export const workspacesApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getCurrentWorkspace: builder.query<
      APIResponse<CurrentWorkspaceResponse>,
      void
    >({
      query: () => ({
        url: 'workspaces/current',
        method: 'GET',
      }),
      keepUnusedDataFor: CacheDurations.MEDIUM,
      providesTags: [{ type: 'CurrentWorkspace', id: 'CURRENT' }],
    }),
    updateCurrentWorkspace: builder.mutation<
      APIResponse<UpdateCurrentWorkspaceResponse>,
      UpdateCurrentWorkspacePayload
    >({
      query: (body) => ({
        url: 'workspaces/current',
        method: 'PUT',
        body,
      }),
      invalidatesTags: [{ type: 'CurrentWorkspace', id: 'CURRENT' }],
    }),
    patchCurrentWorkspace: builder.mutation<
      APIResponse<CurrentWorkspaceResponse>,
      PatchCurrentWorkspacePayload
    >({
      query: (body) => ({
        url: 'workspaces/current',
        method: 'PATCH',
        body,
      }),
      invalidatesTags: [{ type: 'CurrentWorkspace', id: 'CURRENT' }],
    }),
    checkCurrentWorkspacePermission: builder.query<
      APIResponse<WorkspacePermissionCheckResponse>,
      WorkspacePermissionCheckPayload
    >({
      query: ({ action, ...body }) => ({
        url: 'workspaces/current/permissions/check',
        method: 'POST',
        body: {
          ...body,
          action: `can_${action}`,
        },
      }),
    }),
    getWorkspaceIndustries: builder.query<
      APIResponse<WorkspaceIndustryOption[]>,
      void
    >({
      query: () => ({
        url: 'workspaces/industries',
        method: 'GET',
      }),
      keepUnusedDataFor: CacheDurations.XLONG,
    }),
    getWorkspaceCurrencies: builder.query<
      APIResponse<WorkspaceCurrencyOption[]>,
      { query?: string } | void
    >({
      query: (params) => ({
        url: 'workspaces/currencies',
        method: 'GET',
        params: params ?? undefined,
      }),
      keepUnusedDataFor: CacheDurations.XLONG,
    }),
    requestStorageUpload: builder.mutation<
      APIResponse<StorageUploadResponse>,
      StorageUploadRequestPayload
    >({
      query: (body) => ({
        url: 'storage/upload',
        method: 'POST',
        body,
      }),
    }),
    inviteMember: builder.mutation<
      APIResponse<InviteMemberResponse>,
      InviteMemberPayload
    >({
      query: (body) => ({
        url: 'workspaces/invites',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'WorkspaceInvites', id: 'LIST' }],
    }),
    getUserInvites: builder.query<APIResponse<GetUserInvitesResponse>, void>({
      query: () => ({
        url: 'workspaces/user/invites',
        method: 'GET',
      }),
      providesTags: [{ type: 'UserWorkspaceInvites', id: 'LIST' }],
    }),
    acceptInvite: builder.mutation<
      APIResponse<AcceptInviteResponse>,
      AcceptInvitePayload
    >({
      query: (body) => ({
        url: 'workspaces/invites/accept',
        method: 'POST',
        body,
      }),
    }),
    resolveInvite: builder.query<
      APIResponse<WorkspaceInviteResolveResponse>,
      string
    >({
      query: (inviteCode) => ({
        url: `workspaces/invites/resolve/${encodeURIComponent(inviteCode)}`,
        method: 'GET',
      }),
    }),
    createJoinRequest: builder.mutation<
      APIResponse<CreateJoinRequestResponse>,
      CreateJoinRequestPayload
    >({
      query: (body) => ({
        url: 'workspaces/join-requests',
        method: 'POST',
        body,
      }),
    }),
    createWorkspace: builder.mutation<
      APIResponse<CreateWorkspaceResponse>,
      CreateWorkspacePayload
    >({
      query: (body) => ({
        url: 'workspaces',
        method: 'POST',
        body,
      }),
    }),
    reactivateArchivedWorkspace: builder.mutation<
      APIResponse<ReactivateArchivedWorkspaceResponse>,
      string
    >({
      query: (workspaceId) => ({
        url: `workspaces/archived/${workspaceId}/reactivate`,
        method: 'POST',
        body: { confirm: true },
      }),
      invalidatesTags: [
        { type: 'UserWorkspaceInvites', id: 'LIST' },
        { type: 'CurrentUserContext', id: 'CURRENT' },
        { type: 'CurrentWorkspace', id: 'CURRENT' },
      ],
    }),
    getWorkspaces: builder.query<APIResponse<WorkspaceListResponse>, void>({
      query: () => ({
        url: 'workspaces',
        method: 'GET',
      }),
      keepUnusedDataFor: CacheDurations.MEDIUM,
    }),
    getWorkspaceMembers: builder.query<
      APIResponse<GetWorkspaceMembersResponse>,
      GetWorkspaceMembersParams | void
    >({
      query: (params) => ({
        url: 'workspaces/current/members',
        method: 'GET',
        params: params ?? undefined,
      }),
      keepUnusedDataFor: CacheDurations.SHORT,
      providesTags: [{ type: 'WorkspaceMembers', id: 'LIST' }],
    }),
    updateWorkspaceMember: builder.mutation<
      APIResponse<WorkspaceMemberItem>,
      { membershipId: string; payload: UpdateWorkspaceMemberPayload }
    >({
      query: ({ membershipId, payload }) => ({
        url: `workspaces/current/members/${membershipId}`,
        method: 'PATCH',
        body: payload,
      }),
      invalidatesTags: [{ type: 'WorkspaceMembers', id: 'LIST' }],
    }),
    deactivateWorkspaceMember: builder.mutation<APIResponse<null>, string>({
      query: (membershipId) => ({
        url: `workspaces/current/members/${membershipId}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'WorkspaceMembers', id: 'LIST' }],
    }),
    reactivateWorkspaceMember: builder.mutation<
      APIResponse<WorkspaceMemberItem>,
      string
    >({
      query: (membershipId) => ({
        url: `workspaces/current/members/${membershipId}/reactivate`,
        method: 'PUT',
      }),
      invalidatesTags: [{ type: 'WorkspaceMembers', id: 'LIST' }],
    }),
    getWorkspaceInvites: builder.query<
      APIResponse<GetWorkspaceInvitesResponse>,
      GetWorkspaceInvitesParams | void
    >({
      query: (params) => ({
        url: 'workspaces/invites',
        method: 'GET',
        params: params ?? undefined,
      }),
      keepUnusedDataFor: CacheDurations.SHORT,
      providesTags: [{ type: 'WorkspaceInvites', id: 'LIST' }],
    }),
    revokeWorkspaceInvite: builder.mutation<APIResponse<null>, string>({
      query: (inviteId) => ({
        url: `workspaces/invites/${inviteId}/revoke`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'WorkspaceInvites', id: 'LIST' }],
    }),
    getWorkspaceJoinRequests: builder.query<
      APIResponse<GetWorkspaceJoinRequestsResponse>,
      GetWorkspaceJoinRequestsParams | void
    >({
      query: (params) => ({
        url: 'workspaces/join-requests',
        method: 'GET',
        params: params ?? undefined,
      }),
      keepUnusedDataFor: CacheDurations.SHORT,
      providesTags: [{ type: 'WorkspaceJoinRequests', id: 'LIST' }],
    }),
    workspaceJoinRequestDecision: builder.mutation<
      APIResponse<WorkspaceJoinRequestDecisionResponse>,
      { joinRequestId: string; payload: WorkspaceJoinRequestDecisionPayload }
    >({
      query: ({ joinRequestId, payload }) => ({
        url: `workspaces/join-requests/${joinRequestId}/decision`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: [
        { type: 'WorkspaceJoinRequests', id: 'LIST' },
        { type: 'WorkspaceMembers', id: 'LIST' },
      ],
    }),
  }),
});

export const {
  useGetCurrentWorkspaceQuery,
  useUpdateCurrentWorkspaceMutation,
  usePatchCurrentWorkspaceMutation,
  useCheckCurrentWorkspacePermissionQuery,
  useGetWorkspaceIndustriesQuery,
  useLazyGetWorkspaceCurrenciesQuery,
  useRequestStorageUploadMutation,
  useInviteMemberMutation,
  useGetUserInvitesQuery,
  useAcceptInviteMutation,
  useResolveInviteQuery,
  useCreateJoinRequestMutation,
  useCreateWorkspaceMutation,
  useReactivateArchivedWorkspaceMutation,
  useGetWorkspacesQuery,
  useGetWorkspaceMembersQuery,
  useLazyGetWorkspaceMembersQuery,
  useUpdateWorkspaceMemberMutation,
  useDeactivateWorkspaceMemberMutation,
  useReactivateWorkspaceMemberMutation,
  useLazyGetWorkspaceInvitesQuery,
  useRevokeWorkspaceInviteMutation,
  useLazyGetWorkspaceJoinRequestsQuery,
  useWorkspaceJoinRequestDecisionMutation,
} = workspacesApi;
